import {
  InitialConfigType,
  LexicalComposer,
} from "@lexical/react/LexicalComposer";
import { FormHelperText } from "@mui/material";
import editorTheme from "../editor/EditorTheme";
import { ListItemNode, ListNode } from "@lexical/list";
import { LoadInitialContent } from "../editor/LoadInitialContent";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useCallback, useEffect, useState } from "react";
import { $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND } from "lexical";
import { mergeRegister, $getNearestNodeOfType } from "@lexical/utils";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode as ListNodeType,
} from "@lexical/list";
import { $isHeadingNode } from "@lexical/rich-text";
import { $generateHtmlFromNodes } from "@lexical/html";
import { $getRoot } from "lexical";
import {
  UNORDERED_LIST,
  CHECK_LIST,
  ORDERED_LIST,
  HIGHLIGHT,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  STRIKETHROUGH,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
} from "@lexical/markdown";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import "./../editor/Editor.scss";
import { FormikErrors } from "formik";

const EvaluationCommentToolbar = () => {
  const [editor] = useLexicalComposerContext();

  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [blockType, setBlockType] = useState("paragraph");

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNodeType);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          setBlockType(type);
        }
      }

      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsUnderline(selection.hasFormat("underline"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      })
    );
  }, [editor, updateToolbar]);

  return (
    <div className="toolbar">
      <button disabled />
      <button
        type="button"
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
        }}
        className={"toolbar-item spaced " + (isBold ? "active" : "")}
        aria-label="Format Bold"
      >
        B
      </button>
      <button
        type="button"
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
        }}
        className={"toolbar-item spaced " + (isItalic ? "active" : "")}
        aria-label="Format Italics"
      >
        I
      </button>
      <button
        type="button"
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");
        }}
        className={"toolbar-item spaced " + (isUnderline ? "active" : "")}
        aria-label="Format Underline"
      >
        U
      </button>
      <button
        type="button"
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
        }}
        className={"toolbar-item spaced " + (isStrikethrough ? "active" : "")}
        aria-label="Format Strikethrough"
      >
        S
      </button>
      <button
        type="button"
        className={
          blockType === "ul"
            ? "toolbar-item spaced active"
            : "toolbar-item spaced"
        }
        onClick={() => {
          if (blockType !== "ul") {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          } else {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
          }
        }}
        aria-label="Format Bullet List"
      >
        Bullet List
      </button>
      <button
        type="button"
        className={
          blockType === "ol"
            ? "toolbar-item spaced active"
            : "toolbar-item spaced"
        }
        onClick={() => {
          if (blockType !== "ul") {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
          } else {
            editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
          }
        }}
        aria-label="Format Numbered List"
      >
        Numbered List
      </button>
    </div>
  );
};

type EvaluationCommentEditorProps = {
  setFieldValue: Function;
  setFieldTouched: Function;
  labelName: string;
  labelHelperName: string;
  initialValue: string;
  errors?: string | string[] | FormikErrors<any> | FormikErrors<any>[];
};

const EvaluationCommentEditor = (props: EvaluationCommentEditorProps) => {
  const editorConfig: InitialConfigType = {
    namespace: "Editor",
    theme: editorTheme,
    onError(error) {
      throw error;
    },
    nodes: [ListNode, ListItemNode],
  };

  const {
    labelName,
    labelHelperName,
    setFieldValue,
    setFieldTouched,
    initialValue,
    errors
  } = { ...props };

  const placeholder = () => {
    return <div className="editor-placeholder"></div>;
  };

  const [characterCounter, setCharacterCounter] = useState(0);
  const [editorData, setEditorData] = useState<
    | {
        html: string;
        text: string;
      }
    | undefined
  >(undefined);
  const [normalizedError, setNormalizedError] = useState<string>();

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!editorData) return;

      setFieldValue(labelName, editorData.html);
      setFieldValue(labelHelperName, editorData.text);
      setNormalizedError(
        typeof errors === "string"
          ? errors
          : Array.isArray(errors)
          ? errors.join(", ")
          : undefined
      );

      if (editorData.html !== initialValue) {
        setFieldTouched(labelHelperName, true, false);
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [
    editorData,
    initialValue,
    labelHelperName,
    labelName,
    setFieldTouched,
    setFieldValue,
    errors
  ]);

  return (
    <div
      id="eval-comment-editor"
      className={errors ? "editor-error" : ""}
    >
      <LexicalComposer initialConfig={editorConfig}>
        <LoadInitialContent initialContent={props.initialValue} />
        <div className="editor-container editor-container-comment">
          <EvaluationCommentToolbar />
          <div className="editor-inner"></div>
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="editor-input editor-input-comment" />
            }
            placeholder={placeholder}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <OnChangePlugin
            onChange={(state, editor) => {
              state.read(() => {
                const html = $generateHtmlFromNodes(editor, null);
                const text = $getRoot().getTextContent();

                setCharacterCounter(text.length);
                setEditorData({
                  text: text,
                  html: html,
                });
              });
            }}
          />
          <ListPlugin />
          <MarkdownShortcutPlugin
            transformers={[
              UNORDERED_LIST,
              CHECK_LIST,
              ORDERED_LIST,
              HIGHLIGHT,
              BOLD_ITALIC_STAR,
              BOLD_ITALIC_UNDERSCORE,
              BOLD_STAR,
              BOLD_UNDERSCORE,
              STRIKETHROUGH,
              ITALIC_STAR,
              ITALIC_UNDERSCORE,
            ]}
          />
        </div>
      </LexicalComposer>
      {characterCounter < 5000 ? (
        <FormHelperText>
          {5000 - characterCounter} Characters Remaining
        </FormHelperText>
      ) : (
        <FormHelperText error>
          Comments cannot be over 5000 characters.
        </FormHelperText>
      )}
      {errors && (
        <FormHelperText error>{normalizedError}</FormHelperText>
      )}
    </div>
  );
};

export default EvaluationCommentEditor;
